import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { useWikimediaSearch } from '../../hooks/useWikimediaSearch';
import { useImageUpload } from '../../hooks/useImageUpload';
import { compressImageFile } from '../utils/imageCompression';
import { generateSafeFolderName } from '../utils/filenameSanitizer';
import WikimediaSearchBar from './WikimediaSearchBar';
import WikimediaImageGrid from './WikimediaImageGrid';
import WikimediaImagePreview from './WikimediaImagePreview';

/**
 * Wikimedia Commons image search and selection component.
 * Images are fetched at maximum 1024px width to stay within 1024 × 643 pixel constraint.
 */
const WikimediaCommonsTab = ({
  onImageUploaded,
  itineraryMetadata,
  poiName,
  maxSizeKB = 400
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const { searchImages, getImageDetails, downloadImage } = useWikimediaSearch();
  const { uploadImage } = useImageUpload();

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) return;

    setSearchQuery(query);
    setIsSearching(true);
    setError(null);
    setSelectedImage(null);

    try {
      const results = await searchImages(query, 50);

      if (results.length === 0) {
        setError('No images found. Try a different search term.');
      }

      setSearchResults(results);
    } catch (err) {
      setError('Search failed. Please try again.');
      console.error('Search error:', err);
    } finally {
      setIsSearching(false);
    }
  }, [searchImages]);

  // Auto-search when component mounts with POI name and city
  useEffect(() => {
    if (poiName && poiName.trim()) {
      const city = itineraryMetadata?.city;
      const searchTerm = city && city.trim()
        ? `${poiName} ${city}`
        : poiName;
      handleSearch(searchTerm);
    }
  }, [poiName, itineraryMetadata?.city, handleSearch]);

  const handleImageSelect = async (imageTitle) => {
    setError(null);
    setIsSearching(true); // Show loading while fetching details

    try {
      // Request image with max width of 1024px to stay within 1024 × 643 constraint
      const details = await getImageDetails(imageTitle, 1024);
      setSelectedImage(details);
    } catch (err) {
      setError('Failed to load image details. Please try another image.');
      console.error('Image details error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleUseImage = async () => {
    if (!selectedImage) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Download image from Wikimedia
      const imageBlob = await downloadImage(selectedImage.thumbUrl);

      const file = new File([imageBlob], selectedImage.filename, {
        type: selectedImage.mime || 'image/jpeg'
      });

      const optimized = await compressImageFile(file, {
        maxWidth: 1200,
        targetSizeKB: maxSizeKB,
      });

      // Sanitize country and language names
      const sanitizedCountry = itineraryMetadata?.country
        ? itineraryMetadata.country.trim().toLowerCase().replace(/[^\w]/g, '_').replace(/_+/g, '_')
        : '';
      const sanitizedLanguage = itineraryMetadata?.language
        ? itineraryMetadata.language.trim().toLowerCase().replace(/[^\w-]/g, '_')
        : '';
      
      const sanitizeFilename = (name) => {
        return (name || 'itinerary')
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '');
      };

      const canonicalFolderName = itineraryMetadata
        ? generateSafeFolderName(itineraryMetadata)
        : `${sanitizedCountry}_${sanitizedLanguage}_${sanitizeFilename(itineraryMetadata?.name)}`;

      // Upload using existing hook
      const metadata = {
        country: sanitizedCountry,
        language: sanitizedLanguage,
        itineraryName: canonicalFolderName,
        filename: canonicalFolderName ? `${canonicalFolderName}.json` : itineraryMetadata?.filename,
        poiName: poiName,
        wikimediaLicenseShortName: selectedImage.ccData?.licenseShortName || selectedImage.license || '',
        wikimediaLicenseUrl: selectedImage.ccData?.licenseUrl || '',
        wikimediaAttribution: selectedImage.ccData?.attribution || selectedImage.attribution || '',
        wikimediaArtist: selectedImage.ccData?.artist || '',
        wikimediaCredit: selectedImage.ccData?.credit || '',
        wikimediaSource: selectedImage.ccData?.source || '',
        wikimediaPageUrl: selectedImage.ccData?.pageUrl || '',
        wikimediaUsageTerms: selectedImage.ccData?.usageTerms || '',
        wikimediaCcData: selectedImage.ccData || {}
      };

      const uploadedUrl = await uploadImage(optimized.file, metadata, { maxSizeKB });

      // Success - call parent callback
      onImageUploaded(uploadedUrl, selectedImage.ccData);

      // Reset state
      setSearchResults([]);
      setSelectedImage(null);
      setSearchQuery('');
    } catch (err) {
      setError(err.message || 'Failed to process image. Please try again.');
      console.error('Image processing error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToResults = () => {
    setSelectedImage(null);
    setError(null);
  };

  return (
    <div className="space-y-4">
      <WikimediaSearchBar
        onSearch={handleSearch}
        isSearching={isSearching}
        initialQuery={searchQuery}
      />

      {error && (
        <div className="text-red-500 text-sm flex items-start gap-2 p-3 bg-red-50 rounded border border-red-200">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {isSearching && !selectedImage && (
        <div className="text-gray-500 text-center py-8">
          <div className="animate-pulse">Searching Wikimedia Commons...</div>
        </div>
      )}

      {!selectedImage && searchResults.length > 0 && (
        <WikimediaImageGrid
          images={searchResults}
          onImageSelect={handleImageSelect}
        />
      )}

      {selectedImage && (
        <WikimediaImagePreview
          image={selectedImage}
          onUseImage={handleUseImage}
          onBack={searchResults.length > 0 ? handleBackToResults : null}
          isProcessing={isProcessing}
        />
      )}

      {!isSearching &&
        !selectedImage &&
        searchResults.length === 0 &&
        !searchQuery && (
          <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg border border-dashed">
            <p className="mb-2">Search for free images from Wikimedia Commons</p>
            <p className="text-sm text-gray-400">
              Try searching for landmarks, locations, or points of interest
            </p>
          </div>
        )}
    </div>
  );
};

export default WikimediaCommonsTab;
